import { captureException, type ErrorEvent, type Exception, init, setUser } from '@sentry/vue'

import { InMemoryCache } from './cache/InMemoryCache.ts'

interface SentryUser {
  id: number
  email: string
  first_name: string
  last_name: string
}

export const sentry = {
  captureException,
  cache: InMemoryCache.tags('sentry'),

  setUser(user: SentryUser) {
    setUser({
      id: `${user.id}`,
      email: user.email,
      'First name': user.first_name,
      'Last name': user.last_name,
    })
  },

  async init() {
    await stores.initialState.load()
    const dsn = $env.sentry.dsn ?? ''
    const environment = $env.app_name

    if (!dsn) {
      return
    }

    init({
      app,
      dsn,
      environment,
      release: $env.version,
      ignoreErrors: [
        // V_CONFIRM_ERRORS.
        'Cancel button',
        'Escape',
        'Dialog closed',
        'Backspace',

        'cancel',
        'canceled',
        'CanceledError',
        'close',
        'Internal Server Error', // Internal server error. The server errors are reported by the backend.
        'Load failed',
        'Network Error', // Generic network issues on the user's side.
        'Non-Error promise rejection captured with value: cancel', // When a user presses the cancel button. The promise is rejected.
        'Request failed with status code 500', // Internal server error. The server errors are reported by the backend.
        'Service Unavailable', // 503, means the server is down for maintenance.
        'Unsaved changes', // When the user tries to navigate away from a page with unsaved changes.
        'Validation failed', // When the user tries to submit a form with invalid data.
        /AbortError/, // AbortController: These are intended and handled.
        /error loading dynamically imported module/, // Happens when the application is updated and the asset hashes change.
        /Failed to fetch/, // AbortController: These are intended and handled.
        /Fetch is aborted/, // AbortController: These are intended and handled.
        /NetworkError when attempting to fetch resource/, // Generic network issues on the user's side.
        /No error message/,
        /Request aborted due to a new request with the same fingerprint/, // AbortController: These are intended and handled.
        /Request failed with status code 4[0-9]{2}/, // Client errors are expected.
        /ResizeObserver loop.*/i,
        /this.isExpired is not a function/i,
        /Unable to preload CSS for/,
        /Unauthorized route/, // When the user tries to access a route they are not authorized to access.
        /Unprocessable Entity/, // 422.
        /user aborted a request/, // AbortController: These are intended and handled.
      ],
      beforeSend: (event) => this.beforeSend(event),
    })
  },

  beforeSend(event: ErrorEvent) {
    event = this.rateLimit(event)

    if (event.exception?.values?.length === 0) {
      return null
    }

    return event
  },

  rateLimit(event: ErrorEvent) {
    if (event.exception?.values) {
      const exceptionValues: Exception[] = []

      for (const exception of event.exception.values) {
        const key = `${exception.type}:${exception.value}`
        if (this.cache.has(key)) {
          //  eslint-disable-next-line no-console
          console.log('Sentry rate limited:', exception)
        } else {
          this.cache.put(key, '', dayjs().add(1, 'minute'))
          exceptionValues.push(exception)
        }
      }

      event.exception.values = exceptionValues
    }

    return event
  },
}
